import { Component } from '@theme/component';

/**
 * @typedef {object} EngravingConfiguratorRefs
 * @property {HTMLInputElement} textInput
 * @property {HTMLElement} previewText
 * @property {HTMLElement} previewColorLabel
 * @property {HTMLElement} characterCount
 */

class EngravingConfigurator extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.variantPicker = this.closest('.product-details')?.querySelector('variant-picker');
    this.variantPicker?.addEventListener('change', () => {
      this.syncFromVariantPicker();
      this.syncColorAvailability();
      this.updatePreview();
    });
    this.syncColorAvailability();
    this.syncFromVariantPicker();
    this.updatePreview();
  }

  handleTextInput() {
    this.updatePreview();
  }

  handleFontChange() {
    this.updatePreview();
  }

  handleColorChange() {
    this.syncVariantPickerFromConfigurator();
    this.updatePreview();
  }

  handleInstagramSymbolChange() {
    this.updatePreview();
  }

  updatePreview() {
    if (!this.refs.textInput || !this.refs.previewText || !this.refs.characterCount) return;

    const fallbackText = this.dataset.previewText?.trim() || 'Stolen';
    const textValue = this.refs.textInput?.value?.trim() || fallbackText;
    const selectedFont = this.querySelector('input[name="properties[Gravur Schrift]"]:checked');
    const selectedColor = this.querySelector('input[name="properties[Gravur Farbe]"]:checked');
    const selectedInstagramSymbol = this.querySelector('input[name^="engraving-instagram-symbol-"]:checked');
    const preview = this.querySelector('.engraving-preview');
    const showInstagramSymbol = selectedInstagramSymbol?.value === 'Ja';

    this.refs.previewText.textContent = textValue;
    this.refs.previewText.className = `engraving-preview__text engraving-preview__text--${selectedFont?.dataset.previewFont || 'montserrat'}`;
    this.refs.characterCount.textContent = `${this.refs.textInput.value.length} / 20`;

    if (this.refs.previewInstagram) {
      this.refs.previewInstagram.classList.toggle('is-hidden', !showInstagramSymbol);
    }

    if (this.refs.instagramProperty) {
      this.refs.instagramProperty.value = showInstagramSymbol ? 'Ja' : 'Nein';
    }

    if (preview && selectedColor?.dataset.previewColor) {
      preview.dataset.color = selectedColor.dataset.previewColor;
      preview.dataset.size = this.getSizeBucket(textValue.length);
    }

    if (this.refs.previewColorLabel && selectedColor?.dataset.previewLabel) {
      this.refs.previewColorLabel.textContent = selectedColor.dataset.previewLabel;
    }

    const previewImages = this.refs.previewImages;
    if (Array.isArray(previewImages) && selectedColor?.dataset.previewColor) {
      for (const image of previewImages) {
        image.classList.toggle('is-active', image.dataset.imageColor === selectedColor.dataset.previewColor);
      }
    }
  }

  getSizeBucket(length) {
    if (length >= 15) return 'long';
    if (length >= 11) return 'medium';
    return 'short';
  }

  syncVariantPickerFromConfigurator() {
    if (!this.variantPicker) return;

    const selectedColor = this.querySelector('input[name="properties[Gravur Farbe]"]:checked');
    const selectedValue = selectedColor?.dataset.colorValue?.trim().toLowerCase();
    if (!selectedValue) return;

    const select = this.variantPicker.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      const targetOption = Array.from(select.options).find((option) => option.value.trim().toLowerCase() === selectedValue);
      if (targetOption && !targetOption.disabled && select.value !== targetOption.value) {
        select.value = targetOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    const targetRadio = Array.from(this.variantPicker.querySelectorAll('input[type="radio"]')).find(
      (radio) => radio.value.trim().toLowerCase() === selectedValue,
    );

    if (targetRadio instanceof HTMLInputElement && !targetRadio.disabled && !targetRadio.checked) {
      targetRadio.checked = true;
      targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  syncFromVariantPicker() {
    if (!this.variantPicker) return;

    let selectedValue = '';
    const select = this.variantPicker.querySelector('select');
    if (select instanceof HTMLSelectElement) {
      selectedValue = select.value;
    } else {
      const checkedRadio = this.variantPicker.querySelector('input[type="radio"]:checked');
      if (checkedRadio instanceof HTMLInputElement) {
        selectedValue = checkedRadio.value;
      }
    }

    if (!selectedValue) return;
    const normalized = selectedValue.trim().toLowerCase();
    const configuratorInput = Array.from(this.querySelectorAll('input[name="properties[Gravur Farbe]"]')).find(
      (input) => input.dataset.colorValue?.trim().toLowerCase() === normalized,
    );

    if (configuratorInput instanceof HTMLInputElement && !configuratorInput.disabled) {
      configuratorInput.checked = true;
    }
  }

  syncColorAvailability() {
    if (!this.variantPicker) return;

    const availability = new Map();
    const select = this.variantPicker.querySelector('select');

    if (select instanceof HTMLSelectElement) {
      for (const option of select.options) {
        availability.set(option.value.trim().toLowerCase(), !option.disabled && !option.text.toLowerCase().includes('unavailable'));
      }
    } else {
      for (const radio of this.variantPicker.querySelectorAll('input[type="radio"]')) {
        if (!(radio instanceof HTMLInputElement)) continue;
        const isAvailable = radio.getAttribute('aria-disabled') !== 'true' && radio.dataset.optionAvailable !== 'false';
        availability.set(radio.value.trim().toLowerCase(), isAvailable);
      }
    }

    const colorInputs = Array.from(this.querySelectorAll('input[name="properties[Gravur Farbe]"]'));
    let fallbackSelected = null;

    for (const input of colorInputs) {
      const key = input.dataset.colorValue?.trim().toLowerCase();
      const isAvailable = key ? availability.get(key) !== false : true;
      input.disabled = !isAvailable;

      if (isAvailable && !fallbackSelected) {
        fallbackSelected = input;
      }
    }

    const currentSelected = colorInputs.find((input) => input.checked && !input.disabled);
    if (!currentSelected && fallbackSelected instanceof HTMLInputElement) {
      fallbackSelected.checked = true;
    }
  }
}

if (!customElements.get('engraving-configurator-component')) {
  customElements.define('engraving-configurator-component', EngravingConfigurator);
}
